export type PartnerBusinessType = 'studio' | 'theater' | 'independent' | 'distributor';

export type PublisherPartnerFields = {
  business_type?: string | null;
  partner_type?: string | null;
};

function mapPartnerTypeToBusinessType(
  partnerType: string | null | undefined
): PartnerBusinessType | null {
  switch (partnerType) {
    case 'indie_creator':
      return 'independent';
    case 'production_company':
      return 'studio';
    case 'distributor':
      return 'distributor';
    default:
      return null;
  }
}

function isKnownBusinessType(
  businessType: string | null | undefined
): businessType is PartnerBusinessType {
  return (
    businessType === 'theater' ||
    businessType === 'distributor' ||
    businessType === 'independent' ||
    businessType === 'studio'
  );
}

/** Resolve marketplace/onboarding partner mode from publisher fields. */
export function getPartnerBusinessType(
  businessType: string | null | undefined,
  partnerType?: string | null | undefined
): PartnerBusinessType {
  if (isKnownBusinessType(businessType)) {
    return businessType;
  }

  const mapped = mapPartnerTypeToBusinessType(partnerType);
  if (mapped) {
    return mapped;
  }

  // Guild-origin and legacy studios often have null business_type.
  return 'studio';
}

export function getPartnerBusinessTypeFromPublisher(
  publisher: PublisherPartnerFields | null | undefined
): PartnerBusinessType {
  return getPartnerBusinessType(publisher?.business_type, publisher?.partner_type);
}

export function isCinemaPartner(
  businessType: string | null | undefined,
  partnerType?: string | null | undefined
): boolean {
  return getPartnerBusinessType(businessType, partnerType) === 'theater';
}

export function isStudioPartner(
  businessType: string | null | undefined,
  partnerType?: string | null | undefined
): boolean {
  const resolved = getPartnerBusinessType(businessType, partnerType);
  return resolved === 'studio' || resolved === 'independent';
}

export function isDistributorPartner(
  businessType: string | null | undefined,
  partnerType?: string | null | undefined
): boolean {
  return getPartnerBusinessType(businessType, partnerType) === 'distributor';
}

export function theatreMarketplaceNavLabel(
  businessType: string | null | undefined,
  partnerType?: string | null | undefined
): string {
  if (isCinemaPartner(businessType, partnerType)) return 'Book Screenings';
  return 'Theatre Marketplace';
}

export function showTheatreMarketplaceNav(
  businessType: string | null | undefined,
  partnerType?: string | null | undefined
): boolean {
  return isCinemaPartner(businessType, partnerType) || isStudioPartner(businessType, partnerType);
}
